import { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormControl, FormLabel, Grid2 as Grid } from "@mui/material";
import { toast } from "react-toastify";

import { RootState } from "@app/store";
import { useAppDispatch } from "@features/common/StoreHook";
import { formControlStyle, formLabelStyle } from "@features/common/styles";
import FormTextField from "@features/common/FormFields/FormTextField";
import FormTextArea from "@features/common/FormFields/FormTextArea";
import EditButtonGroup from "@features/common/ButtonGroups/EditButtonGroup";
import {
  getCollectionById,
  selectCollectionsStatus,
  updateCollection,
} from "./collectionsSlice";
import CollectionValues from "./CollectionValues";

const validationSchema = yup.object({
  name: yup.string().required(),
  description: yup.string().nullable(),
});

interface CollectionEditProps {
  collectionId: string;
}

const CollectionEdit = (props: CollectionEditProps) => {
  const { collectionId } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const collection = useSelector((state: RootState) =>
    getCollectionById(state, collectionId),
  );
  const isSubmitting = useSelector(selectCollectionsStatus) === "submitting";

  const { handleSubmit, control, reset } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: collection?.name,
      description: collection?.description,
    },
  });

  const handleUpdate = (formData: any) => {
    dispatch(updateCollection(formData))
      .unwrap()
      .then(() => {
        setIsEditing(false);
        toast.success("Collection updated successfully");
      })
      .catch(() => {
        toast.error("Failed to update collection");
      });
  };

  const handleCancel = () => {
    reset({
      name: collection?.name,
      description: collection?.description,
    });
    setIsEditing(false);
  };

  return (
    <>
      <FormControl component="fieldset" sx={formControlStyle}>
        <FormLabel component="legend" sx={formLabelStyle}>
          {t("label-collection")}
        </FormLabel>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormTextField
              name="name"
              control={control}
              label={t("label-collection-name")}
              readOnly={true}
            />
          </Grid>
          <Grid size={12}>
            <FormTextArea
              name="description"
              control={control}
              label={t("label-collection-description")}
              readOnly={!isEditing || isSubmitting}
              rows={4}
            />
          </Grid>
          <Grid size={12} sx={{ textAlign: "right" }}>
            <EditButtonGroup
              disabled={isSubmitting}
              isEditing={isEditing}
              handleEdit={() => setIsEditing(true)}
              handleCancel={handleCancel}
              handleSave={handleSubmit(handleUpdate)}
            />
          </Grid>
          <Grid size={12}>
            {collection && (
              <CollectionValues
                collectionMembers={collection?.members}
                collectionId={collection?.name}
              />
            )}
          </Grid>
        </Grid>
      </FormControl>
    </>
  );
};

export default CollectionEdit;
